import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Heart, DollarSign, Users, CreditCard, User, LogOut,
  ArrowRight, CheckCircle2, Clock, AlertCircle,
  Gift, Copy, Award, Utensils, TrendingUp, Download, Loader2, ExternalLink
} from 'lucide-react';
import Logo from '../Logo';
import { HelpTooltip } from '../shared/HelpTooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CharityDashboard({ charity, onLogout }) {
  const [summary, setSummary] = useState(null);
  const [retailers, setRetailers] = useState({ count: 0, retailers: [] });
  const [referral, setReferral] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('charity_session_token')}`,
    'Content-Type': 'application/json'
  });

  const loadDashboardData = async () => {
    try {
      const [s, r, ref, bm, im] = await Promise.all([
        fetch(`${API_URL}/api/charity-portal/donations/summary`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/charity-portal/donations/retailers`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/charity-portal/referral`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/charity-portal/benchmark`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/charity-portal/impact`, { headers: getAuthHeaders() }),
      ]);
      if (s.ok) setSummary(await s.json());
      if (r.ok) setRetailers(await r.json());
      if (ref.ok) setReferral(await ref.json());
      if (bm.ok) setBenchmark(await bm.json());
      if (im.ok) setImpact(await im.json());
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/charity-portal/logout`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    onLogout();
  };

  const copyReferral = async () => {
    if (!referral?.referral_link) return;
    try {
      await navigator.clipboard.writeText(referral.referral_link);
      toast.success('Referral link copied to clipboard!');
    } catch (e) {
      toast.error('Could not copy — please copy it manually.');
    }
  };

  const downloadReferralAsset = async (type) => {
    const url = type === 'flyer'
      ? `${API_URL}/api/charity-portal/referral/flyer`
      : `${API_URL}/api/charity-portal/referral/qr`;
    setDl(type);
    try {
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = type === 'flyer' ? 'DealShaq_Charity_Flyer.pdf' : 'DealShaq_Charity_QR.png';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error('Could not download — please try again.');
    } finally {
      setDl('');
    }
  };

  const getVerificationBadge = () => {
    const status = charity?.onboarding_status || 'pending';
    switch (status) {
      case 'verified':
        return <Badge className="bg-emerald-500"><CheckCircle2 className="w-3 h-3 mr-1" /> Verified</Badge>;
      case 'under_review':
        return <Badge className="bg-yellow-500"><Clock className="w-3 h-3 mr-1" /> Under Review</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500"><AlertCircle className="w-3 h-3 mr-1" /> Action Required</Badge>;
      default:
        return <Badge className="bg-gray-500"><Clock className="w-3 h-3 mr-1" /> Setup Required</Badge>;
    }
  };

  const topPct = benchmark?.available ? Math.max(1, 100 - benchmark.donations_percentile) : null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="charity-dashboard">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="small" />
            <div className="hidden sm:block">
              <h1 className="font-semibold text-gray-900">{charity?.name || 'DealShaq Charity App'}</h1>
              <p className="text-xs text-gray-500">DealShaq Charity Impact Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getVerificationBadge()}
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="charity-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-2xl font-bold">Welcome, {charity?.name}!</h2>
                <HelpTooltip portal="charity" category="dashboard" tooltipKey="main" size="md" className="text-white/70 hover:text-white" />
              </div>
              <p className="text-emerald-100">
                Track your donations, contributors and community impact.
              </p>
              {benchmark?.available && (
                <div
                  className="inline-flex items-center gap-2 mt-3 bg-white/15 backdrop-blur rounded-full px-3 py-1"
                  data-testid="charity-benchmark-badge"
                >
                  <Award className="w-4 h-4 text-amber-200" />
                  <span className="text-sm font-medium">
                    Top {topPct}% of DealShaq charity partners by charitable contributions
                  </span>
                </div>
              )}
            </div>
            <Heart className="w-12 h-12 text-emerald-200 opacity-50" />
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="stat-total-donations">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Charitable Contributions</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    ${summary?.total_charitable_contributions?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-unique-contributors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">People Who Contributed</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {summary?.unique_contributors ?? 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-supporting-retailers">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Supporting Retailers</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {retailers?.count || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-pending-payout">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Payout</p>
                  <p className="text-2xl font-bold text-orange-600">
                    ${summary?.pending_payout?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Impact + Referral row */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Community Impact */}
          <Card data-testid="charity-impact-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Utensils className="w-5 h-5 text-emerald-600" />
                Your Community Impact
              </CardTitle>
              <CardDescription>
                Food rescued through orders that chose your organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-700">
                    {(impact?.charitable_meals_funded ?? 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-600">meals funded by donations</p>
                </div>
                <div className="p-4 bg-teal-50 rounded-lg">
                  <p className="text-2xl font-bold text-teal-700">
                    {(impact?.lbs_diverted ?? 0).toLocaleString()} lbs
                  </p>
                  <p className="text-xs text-gray-600">food diverted from landfill</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Meals estimated using Feeding America's $1 ≈ 10 meals ratio.
              </p>
            </CardContent>
          </Card>

          {/* Referral / Grow your base */}
          <Card className="border-emerald-200" data-testid="charity-referral-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Gift className="w-5 h-5 text-emerald-600" />
                Grow Your Support Base
              </CardTitle>
              <CardDescription>
                Invite your community to shop on DealShaq — every order sends donations back to you
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                {referral?.qr_data_url && (
                  <img
                    src={referral.qr_data_url}
                    alt="Scan to sign up and support this charity"
                    className="w-32 h-32 rounded-lg border p-1 bg-white flex-shrink-0"
                    data-testid="charity-referral-qr"
                  />
                )}
                <div className="flex-1 w-full space-y-3">
                  <p className="text-sm text-gray-600">
                    Share this QR with your pantry clients — a phone-camera scan opens signup already linked to you.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={referral?.referral_link || ''}
                      className="flex-1 text-sm bg-gray-50 border rounded-lg px-3 py-2 text-gray-700 truncate"
                      data-testid="charity-referral-link"
                    />
                    <Button size="sm" variant="outline" onClick={copyReferral} data-testid="charity-copy-referral-btn">
                      <Copy className="w-4 h-4 mr-1" /> Copy
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => downloadReferralAsset('qr')}
                      disabled={dl === 'qr'}
                      data-testid="charity-download-qr-btn"
                    >
                      {dl === 'qr' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                      QR image
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => downloadReferralAsset('flyer')}
                      disabled={dl === 'flyer'}
                      data-testid="charity-download-flyer-btn"
                    >
                      {dl === 'flyer' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                      Printable flyer
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => referral?.referral_link && window.open(referral.referral_link, '_blank', 'noopener')}
                      disabled={!referral?.referral_link}
                      data-testid="charity-preview-landing-btn"
                      title="See the signup page your recruits will land on"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Preview landing page
                    </Button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center pt-3 border-t">
                <div>
                  <p className="text-xl font-bold text-gray-900" data-testid="referral-households">{referral?.households_reached ?? 0}</p>
                  <p className="text-xs text-gray-500">households reached</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900" data-testid="referral-active">{referral?.active_shoppers ?? 0}</p>
                  <p className="text-xs text-gray-500">active shoppers</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-emerald-600" data-testid="referral-contributions">
                    ${(referral?.total_charitable_contributions ?? 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">Total Charitable Contributions</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link to="/charity/payment">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  Payment Setup
                </CardTitle>
                <CardDescription>
                  Connect Stripe or add bank details to receive payouts
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full">
                  Setup Payments <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </Link>

          <Link to="/charity/donations">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Donations & Reports
                </CardTitle>
                <CardDescription>
                  Contribution sources, monthly & quarterly trends, payout ledger, receipts & tax statements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full">
                  View Donations <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </Link>

          <Link to="/charity/profile">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="w-5 h-5 text-purple-600" />
                  Organization Profile
                </CardTitle>
                <CardDescription>
                  Update your organization info and contact details
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full">
                  Edit Profile <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Supporting Retailers */}
        {retailers?.retailers?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" />
                Retailers Supporting You
              </CardTitle>
              <CardDescription>
                These retailers have designated your organization as their charity partner
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {retailers.retailers.slice(0, 6).map((retailer) => (
                  <div
                    key={retailer.id}
                    className="p-3 bg-gray-50 rounded-lg flex items-center gap-3"
                  >
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <span className="text-emerald-600 font-semibold">
                        {(retailer.store_name || retailer.name || 'R')[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{retailer.store_name || retailer.name}</p>
                      <p className="text-xs text-emerald-600 font-semibold">${(retailer.total_contributed ?? 0).toFixed(2)} contributed</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Onboarding Prompt */}
        {(!charity?.payment_method || charity?.onboarding_status === 'pending') && (
          <Card className="border-2 border-dashed border-emerald-200 bg-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Complete Your Setup</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Set up your payment method to start receiving donations. We recommend connecting with Stripe for instant, automated payouts.
                  </p>
                  <Link to="/charity/payment">
                    <Button className="bg-emerald-600 hover:bg-emerald-700">
                      Setup Payment Method
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
